import { isArray, isEmpty, isEqual } from 'lodash-es';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { isDate, isNumber, isObject } from '../fields/field-validation.helper';

export function HasMetadataChanged(
    originalValue: FormValue<any> | FormValue<any>[],
    formDataValue: FormValue<any> | FormValue<any>[]
): boolean {
    if (!isArray(formDataValue) || !isArray(originalValue)) {
        // Just perform regular isEqual on non-lists
        return !isEqual(originalValue, formDataValue);
    }

    const hasChangedIndeterminates = HasChangedIndeterminates(originalValue, formDataValue);

    // No need to further validate if indeterminates are changed in any way
    if (hasChangedIndeterminates) {
        return true;
    }

    // Getters of list values only return non-empty SET FormValues, so omit UNSET values from the form-data for comparison

    const cleanFormDataValue = CleanFormDataValue(formDataValue);

    // HACK: Some field values are initialy generated as an array typed FormValue, only to later be converted to a single object.
    // To detect changes here we also convert the original typed FormValue to a single object FormValue and check.
    const containsTypedFormValues: boolean = ContainsTypedFormValues(originalValue);
    const allTypedFormValues: boolean = AllTypedFormValues(originalValue);

    if (containsTypedFormValues && allTypedFormValues) {
        const singleOriginalObject: any = {};

        originalValue
            .filter(formValue => formValue.state === FormValueState.SET)
            .forEach(formValue => {
                singleOriginalObject[formValue.value.type] = formValue.value.value;
            });

        const originalObjectValue = [new FormValue(singleOriginalObject)];

        // Only if none match will it be detected as changed
        return (
            !IsEqualFormValueArray(originalObjectValue, cleanFormDataValue) &&
            !IsEqualFormValueArray(originalValue, cleanFormDataValue)
        );
    }

    return !IsEqualFormValueArray(originalValue, cleanFormDataValue);
}

function HasChangedIndeterminates(originalValues: FormValue<any>[], formValues: FormValue<any>[]): boolean {
    const originalIndeterminates = originalValues.filter(formValue => formValue.state === FormValueState.INDETERMINATE);
    const formValueIndeterminates = formValues.filter(formValue => formValue.state === FormValueState.INDETERMINATE);

    return originalIndeterminates.length !== formValueIndeterminates.length;
}

function ContainsTypedFormValues(formValues: FormValue<any>[]): boolean {
    return formValues.some(
        formValue =>
            formValue.value &&
            Object.keys(formValue.value).includes('type') &&
            Object.keys(formValue.value).includes('value')
    );
}

function AllTypedFormValues(formValues: FormValue<any>[]): boolean {
    return formValues.every(
        formValue =>
            formValue.value &&
            Object.keys(formValue.value).includes('type') &&
            Object.keys(formValue.value).includes('value')
    );
}

function CleanFormDataValue(formDataValue: FormValue<any>[]): FormValue<any>[] {
    const onlyNonEmptySetData: FormValue<any>[] = formDataValue.filter(
        formValue => formValue.state === FormValueState.SET
    );

    // On read only object properties are generated for non-empty subfields, so filtering these empty properties out from form data
    const filterEmptyProperties: FormValue<any>[] = onlyNonEmptySetData.map(formValue => {
        // Do nothing if not an object
        if (!(typeof formValue.value === 'object' && !Array.isArray(formValue.value) && formValue.value !== null)) {
            return formValue;
        }

        if (IsEmptyObject(formValue.value)) {
            return formValue;
        }
        const newObject: any = {};

        Object.entries(formValue.value).forEach(([key, value]) => {
            if (!IsEmptyObject(value)) {
                newObject[key] = value;
            }
        });

        return new FormValue(newObject, formValue.state);
    });

    return filterEmptyProperties;
}

function IsEmptyObject(value: any): boolean {
    // Check if date or number
    if (isDate(value) || isNumber(value)) {
        return false;
    }

    // Null, empty string, empty array or empty object
    if (!value || isEmpty(value) || (isArray(value) && value.length === 0) || isEqual(value, {})) {
        return true;
    }

    if (isObject(value)) {
        // All properties of object are empty
        return Object.entries(value).every(([key, value]) => IsEmptyObject(value));
    }

    return false;
}

function IsEqualFormValueArray(array1: FormValue<any>[], array2: FormValue<any>[]): boolean {
    const unsetValuesArr1: any[] = array1
        .filter(formValue => formValue.state === FormValueState.UNSET)
        .map(formValue => formValue.value);
    const unsetValuesArr2: any[] = array2
        .filter(formValue => formValue.state === FormValueState.UNSET)
        .map(formValue => formValue.value);

    const setValuesArr1: any[] = array1
        .filter(formValue => formValue.state === FormValueState.SET)
        .map(formValue => formValue.value);
    const setValuesArr2: any[] = array2
        .filter(formValue => formValue.state === FormValueState.SET)
        .map(formValue => formValue.value);

    // Indeterminate values are irrelevant
    return ArrayDeepEqual(unsetValuesArr1, unsetValuesArr2) && ArrayDeepEqual(setValuesArr1, setValuesArr2);
}

// Only arrays of simple interfaces
function ArrayDeepEqual(arr1: any[], arr2: any[]): boolean {
    const sameLength = arr1.length === arr2.length;

    if (!sameLength) {
        return false;
    }

    return (
        arr1.every(arr1El => arr2.find(arr2El => isEqual(arr2El, arr1El))) &&
        arr2.every(arr2El => arr1.find(arr1El => isEqual(arr1El, arr2El)))
    );
}
